import React, { createContext, useContext, useRef, useCallback } from 'react';

interface PermissionsContextType {
  setRefreshCallback: (callback: (() => Promise<void>) | null) => void;
  triggerRefresh: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const refreshCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const setRefreshCallback = useCallback((callback: (() => Promise<void>) | null) => {
    refreshCallbackRef.current = callback;
  }, []);

  const triggerRefresh = useCallback(async () => {
    if (refreshCallbackRef.current && typeof refreshCallbackRef.current === 'function') {
      try {
        await refreshCallbackRef.current();
      } catch (error) {
        console.error('[PermissionsContext] Error in refresh callback:', error);
      }
    }
  }, []);

  const value = React.useMemo(
    () => ({ setRefreshCallback, triggerRefresh }),
    [setRefreshCallback, triggerRefresh]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsRefresh = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissionsRefresh must be used within PermissionsProvider');
  }
  return context;
};

export const usePermissionsRefreshTrigger = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissionsRefreshTrigger must be used within PermissionsProvider');
  }
  return {
    triggerPermissionsRefresh: context.triggerRefresh,
  };
};
